(() => {
  const selector = '[data-arlaya-yml]';

  const randomIndex = (max) => {
    if (max <= 1) return 0;

    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(1);
      window.crypto.getRandomValues(values);
      return values[0] % max;
    }

    return Math.floor(Math.random() * max);
  };

  const shuffle = (items) => {
    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = randomIndex(index + 1);
      [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    }

    return items;
  };

  const getVisibleCards = (track) => (
    Array.from(track.querySelectorAll('[data-yml-card]')).filter((card) => !card.hidden)
  );

  const getColumnPositions = (track) => {
    const positions = getVisibleCards(track)
      .map((card) => Math.max(0, Math.round(card.offsetLeft)))
      .sort((a, b) => a - b);

    return positions.filter((position, index) => index === 0 || Math.abs(position - positions[index - 1]) > 3);
  };

  const updateProgress = (section, track, maximum) => {
    const progress = section.querySelector('[data-yml-progress]');
    if (!progress) return;

    const rail = progress.parentElement;
    const railWidth = rail?.clientWidth || 0;

    if (!railWidth || track.scrollWidth <= 0) {
      progress.style.width = '0px';
      progress.style.transform = 'translateX(0px)';
      return;
    }

    const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
    const thumbWidth = Math.max(34, Math.round(railWidth * visibleRatio));
    const maxThumbOffset = Math.max(0, railWidth - thumbWidth);
    const progressRatio = maximum > 0 ? Math.min(1, Math.max(0, track.scrollLeft / maximum)) : 0;
    const thumbOffset = Math.round(maxThumbOffset * progressRatio);

    progress.style.width = `${thumbWidth}px`;
    progress.style.transform = `translateX(${thumbOffset}px)`;
  };

  const updateNavigation = (section) => {
    const track = section.querySelector('[data-yml-track]');
    if (!track) return;

    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maximum > 4;
    const atStart = !hasOverflow || track.scrollLeft <= 4;
    const atEnd = !hasOverflow || track.scrollLeft >= maximum - 4;

    section.querySelectorAll('[data-yml-previous]').forEach((button) => {
      button.hidden = !hasOverflow;
      button.disabled = atStart;
    });

    section.querySelectorAll('[data-yml-next]').forEach((button) => {
      button.hidden = !hasOverflow;
      button.disabled = atEnd;
    });

    const mobileNavigation = section.querySelector('[data-yml-mobile-navigation]');
    if (mobileNavigation) {
      mobileNavigation.hidden = !hasOverflow;
    }

    updateProgress(section, track, maximum);
  };

  const scrollToAdjacentColumn = (track, direction) => {
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const current = Math.min(maximum, Math.max(0, track.scrollLeft));
    const positions = getColumnPositions(track).map((position) => Math.min(maximum, position));

    let target = direction > 0 ? maximum : 0;

    if (direction > 0) {
      const nextPosition = positions.find((position) => position > current + 4);
      if (typeof nextPosition === 'number') target = nextPosition;
    } else {
      const previousPositions = positions.filter((position) => position < current - 4);
      if (previousPositions.length) target = previousPositions[previousPositions.length - 1];
    }

    track.scrollTo({ left: target, behavior: 'smooth' });
  };

  const updateCardFromSwatch = (button) => {
    const card = button.closest('[data-yml-card]');
    if (!card || button.disabled) return;

    card.querySelectorAll('[data-yml-swatch]').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', swatch === button ? 'true' : 'false');
    });

    const image = card.querySelector('.arlaya-yml__image');
    const links = card.querySelectorAll('[data-yml-link]');
    const currentPrice = card.querySelector('[data-yml-current-price]');
    const comparePrice = card.querySelector('[data-yml-compare-price]');
    const discount = card.querySelector('[data-yml-discount]');

    if (image && button.dataset.image) {
      image.style.opacity = '0.35';
      image.removeAttribute('srcset');
      image.src = button.dataset.image;
      image.onload = () => {
        image.style.opacity = '1';
      };
    }

    if (button.dataset.url) {
      links.forEach((link) => {
        link.href = button.dataset.url;
      });
    }

    if (currentPrice && button.dataset.price) {
      currentPrice.textContent = button.dataset.price;
    }

    if (comparePrice) {
      if (button.dataset.comparePrice) {
        comparePrice.textContent = button.dataset.comparePrice;
        comparePrice.hidden = false;
        currentPrice?.classList.remove('arlaya-yml__price-current--regular');
      } else {
        comparePrice.hidden = true;
        currentPrice?.classList.add('arlaya-yml__price-current--regular');
      }
    }

    if (discount) {
      if (button.dataset.discount && Number(button.dataset.discount) > 0) {
        discount.textContent = `-${button.dataset.discount}%`;
        discount.hidden = false;
      } else {
        discount.hidden = true;
      }
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset.ymlInitialized === 'true') return;

    const track = section.querySelector('[data-yml-track]');
    if (!track) return;

    const cards = shuffle(Array.from(track.querySelectorAll('[data-yml-card]')));
    const limit = Math.max(1, Number.parseInt(section.dataset.limit || '6', 10));

    cards.forEach((card, index) => {
      card.hidden = index >= limit;
      track.appendChild(card);
    });

    section.dataset.ymlInitialized = 'true';
    section.classList.add('arlaya-yml--ready');

    section.querySelectorAll('[data-yml-previous]').forEach((button) => {
      button.addEventListener('click', () => scrollToAdjacentColumn(track, -1));
    });

    section.querySelectorAll('[data-yml-next]').forEach((button) => {
      button.addEventListener('click', () => scrollToAdjacentColumn(track, 1));
    });

    let scrollFrame = null;
    track.addEventListener('scroll', () => {
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => updateNavigation(section));
    }, { passive: true });

    section.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-yml-swatch]');
      if (swatch) updateCardFromSwatch(swatch);
    });

    window.requestAnimationFrame(() => updateNavigation(section));

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => updateNavigation(section));
      observer.observe(track);
      const rail = section.querySelector('.arlaya-yml__progress-rail');
      if (rail) observer.observe(rail);
    } else {
      window.addEventListener('resize', () => updateNavigation(section));
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll(selector).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
